import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostBinding } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TuiLink, TuiIcon } from '@taiga-ui/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { RoleService } from '../../services/role.service';

@Component({
  selector: 'app-notification-banner',
  standalone: true,
  imports: [TuiLink, TuiIcon, CommonModule],
  templateUrl: './notification-banner.html',
  styleUrl: './notification-banner.scss'
})
export class NotificationBanner implements OnInit, OnDestroy {
  userName: string = '';
  planName: string = '';
  trialDaysRemaining: number = 0;
  isLoading: boolean = true;
  shouldShowBanner: boolean = false;
  isExpired: boolean = false;
  hasNoActivePlan: boolean = false;
  expiryDate: string = '';
  // MVE usage warning
  showMveWarning: boolean = false;
  mveUsagePercent: number = 0;
  private parkingChangeHandler?: (event: Event) => void;
  private subscriptionUpdatedHandler?: (event: Event) => void;
  private roleChangeSubscription?: Subscription;

  @HostBinding('style.display')
  get hostDisplay(): string {
    return (!this.isLoading && this.shouldShowBanner) ? 'block' : 'none';
  }

  constructor(
    private router: Router,
    private apiService: ApiService,
    private roleService: RoleService,
    private cdr: ChangeDetectorRef
  ) { }

  get formattedPlanName(): string {
    if (!this.planName) return '';
    let name = this.planName.trim();
    if (name.toLowerCase() === 'plan') return '';
    if (name.toLowerCase().endsWith(' plan')) {
      name = name.substring(0, name.length - 5).trim();
    }
    return name;
  }

  private isExcludedUrl(url: string): boolean {
    const lower = (url || '').toLowerCase();
    return (
      lower.startsWith('/parkings/create') ||
      lower.includes('/login') ||
      lower.includes('/signup') ||
      lower.includes('/no-organization') ||
      lower.includes('/no-parking') ||
      lower.includes('/not-found') ||
      lower.includes('/network-error') ||
      lower.includes('/parking-lots') ||
      lower.startsWith('/organization') ||
      lower.includes('/organization/') ||
      lower.startsWith('/organisations') ||
      lower.includes('/organisations/') ||
      lower.startsWith('/admin') ||
      lower.includes('/admin/') ||
      lower.startsWith('/admin-dashboard') ||
      lower.startsWith('/live-dashboard') ||
      lower.startsWith('/super-admin') ||
      !this.isParkingView()
    );
  }

  private isParkingView(): boolean {
    const currentUrl = (this.router.url || '').toLowerCase();
    const isParkingUrl = currentUrl.startsWith('/parking/') ||
      currentUrl.includes('/parking/') ||
      currentUrl.startsWith('/vendor/') ||
      currentUrl.includes('/vendor/');

    const userPov = (sessionStorage.getItem('user_pov') || localStorage.getItem('user_pov') || '').toLowerCase();
    const isParkingPov = userPov === 'parking' || userPov === 'vendor';

    const currentRole = (this.roleService.getCurrentRole() || '').toLowerCase();
    const isVendorRole = currentRole === 'vendor';

    // Must be in parking route, parking POV, or vendor role, and NOT in organization POV
    if (userPov === 'organization' || userPov === 'admin') {
      return false;
    }

    return isParkingUrl || isParkingPov || isVendorRole;
  }

  ngOnInit(): void {
    // Skip banner logic on create flow where no parking is selected yet, auth pages, or no-parking/no-organization pages
    const currentUrl = this.router.url || '';
    if (this.isExcludedUrl(currentUrl)) {
      this.shouldShowBanner = false;
      this.isLoading = false;
      window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
        detail: { isVisible: false }
      }));
      return;
    }

    this.loadUserAndSubscriptionData();

    // Listen for navigation changes to ensure banner state is consistent across pages
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Small delay to ensure Sidenavigation has finished syncing context to storage
      setTimeout(() => {
        this.loadUserAndSubscriptionData();
      }, 300);
    });

    // Listen for parking change events
    this.setupParkingChangeListener();

    // Listen for subscription updates from Sidenavigation
    this.setupSubscriptionUpdateListener();

    // Listen for role changes to hide banner when switching to super admin
    this.setupRoleChangeListener();
  }

  ngOnDestroy(): void {
    // Remove event listeners when component is destroyed
    if (this.parkingChangeHandler) {
      window.removeEventListener('parkingChanged', this.parkingChangeHandler);
    }
    if (this.subscriptionUpdatedHandler) {
      window.removeEventListener('parkingSubscriptionUpdated', this.subscriptionUpdatedHandler);
    }

    // Unsubscribe from role changes
    if (this.roleChangeSubscription) {
      this.roleChangeSubscription.unsubscribe();
    }
  }

  private setupParkingChangeListener(): void {
    this.parkingChangeHandler = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.parkingId) {
        this.isLoading = true;
        this.shouldShowBanner = false;
        this.loadUserAndSubscriptionData();
      }
    };

    window.addEventListener('parkingChanged', this.parkingChangeHandler);
  }

  private setupSubscriptionUpdateListener(): void {
    this.subscriptionUpdatedHandler = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.parkingId) {
        const parkingId = customEvent.detail.parkingId;
        const currentParkingId = this.getCurrentParkingId();
        if (!currentParkingId || currentParkingId === parkingId) {
          if (customEvent.detail.subInfo) {
            this.planName = customEvent.detail.subInfo.plan_name || '';
            const subData = {
              ...customEvent.detail.subInfo,
              isExpired: customEvent.detail.isExpired
            };
            this.processSubscriptionData(subData, parkingId);
          } else {
            this.loadUserAndSubscriptionData();
          }
        }
      }
    };

    window.addEventListener('parkingSubscriptionUpdated', this.subscriptionUpdatedHandler);
  }

  private setupRoleChangeListener(): void {
    this.roleChangeSubscription = this.roleService.currentRole$.subscribe(() => {
      const currentRole = (this.roleService.getCurrentRole() || '').toLowerCase();
      const currentUser: any = this.apiService.getCurrentUser() || this.apiService.userSignal();
      const currentUrl = this.router.url || '';
      if (currentUser?.type === 'super-admin' || currentRole === 'super admin' || this.isExcludedUrl(currentUrl)) {
        this.shouldShowBanner = false;
        this.isLoading = false;
        this.cdr.detectChanges();
        window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
          detail: { isVisible: false }
        }));
      } else {
        this.loadUserAndSubscriptionData();
      }
    });
  }

  private loadUserAndSubscriptionData(): void {
    const currentUrl = this.router.url || '';
    if (this.isExcludedUrl(currentUrl)) {
      this.shouldShowBanner = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
        detail: { isVisible: false }
      }));
      return;
    }

    const currentRole = (this.roleService.getCurrentRole() || '').toLowerCase();
    const currentUser: any = this.apiService.getCurrentUser() || this.apiService.userSignal();

    // Check if user is in Super Admin
    if (currentUser?.type === 'super-admin' || currentRole === 'super admin') {
      this.shouldShowBanner = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
        detail: { isVisible: false }
      }));
      return;
    }

    const parkingId = this.getCurrentParkingId();
    if (!parkingId || parkingId.trim() === '') {
      this.shouldShowBanner = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
        detail: { isVisible: false }
      }));
      return;
    }

    // Get user information
    if (currentUser) {
      const name = currentUser.firstName || currentUser.first_name || currentUser.name || '';
      this.userName = name.trim() || 'User';
    } else {
      this.userName = 'User';
    }

    // Synchronize current parking ID in storage if urlParkingId was used
    const urlParkingId = this.getCurrentUrlParkingId();
    if (urlParkingId && urlParkingId !== sessionStorage.getItem('parking_id')) {
      sessionStorage.setItem('parking_id', urlParkingId);
      sessionStorage.setItem('selectedParkingId', urlParkingId);
      localStorage.setItem('parking_id', urlParkingId);
    }

    // Fast-path: Check if subscription info is already cached in localStorage/sessionStorage
    const cachedSubStr = sessionStorage.getItem(`parking_subscription_${parkingId}`) ||
      localStorage.getItem(`parking_subscription_${parkingId}`);
    if (cachedSubStr) {
      try {
        const cachedSub = JSON.parse(cachedSubStr);
        if (cachedSub) {
          this.planName = cachedSub.plan_name || '';
          this.processSubscriptionData(cachedSub, parkingId);
          // Also run MVE check
          this.checkMveUsage(parkingId);
        }
      } catch (e) { }
    }

    // Get subscription and plan information directly from parking detail API
    this.apiService.getParkingDetail(parkingId).subscribe({
      next: (response: any) => {
        let parkingData = null;
        if (response && (response.data || response)) {
          parkingData = response.data || response;

          if (parkingData.subscription) {
            const subscription = parkingData.subscription;
            let detectedPlanName = subscription.plan?.name || '';

            if (!detectedPlanName && subscription.plan_id) {
              this.apiService.getPlan(subscription.plan_id).subscribe({
                next: (planRes: any) => {
                  const fetchedName = planRes?.data?.name || planRes?.name || '';
                  if (fetchedName) {
                    this.planName = fetchedName;
                    const subInfo = {
                      status: subscription.status,
                      is_trial: subscription.is_trial,
                      trial_end_date: subscription.trial_end_date,
                      next_billing_date: subscription.next_billing_date,
                      end_date: subscription.end_date,
                      plan_name: fetchedName
                    };
                    localStorage.setItem(`parking_subscription_${parkingId}`, JSON.stringify(subInfo));
                    sessionStorage.setItem(`parking_subscription_${parkingId}`, JSON.stringify(subInfo));
                    this.cdr.detectChanges();
                  }
                }
              });
            }

            const subInfo = {
              status: subscription.status,
              is_trial: subscription.is_trial,
              trial_end_date: subscription.trial_end_date,
              next_billing_date: subscription.next_billing_date,
              end_date: subscription.end_date,
              plan_name: detectedPlanName || 'Plan'
            };
            localStorage.setItem(`parking_subscription_${parkingId}`, JSON.stringify(subInfo));
            sessionStorage.setItem(`parking_subscription_${parkingId}`, JSON.stringify(subInfo));

            this.planName = detectedPlanName;
            this.processSubscriptionData(subscription, parkingId);
          } else {
            localStorage.removeItem(`parking_subscription_${parkingId}`);
            sessionStorage.removeItem(`parking_subscription_${parkingId}`);
            localStorage.removeItem(`parking_banner_state_${parkingId}`);
            sessionStorage.removeItem(`parking_banner_state_${parkingId}`);
            this.hasNoActivePlan = true;
            this.isExpired = false;
            this.planName = '';
            this.trialDaysRemaining = 0;

            const currentUrl = this.router.url || '';
            if (this.isExcludedUrl(currentUrl)) {
              this.shouldShowBanner = false;
            } else {
              this.shouldShowBanner = true;
            }
            this.isLoading = false;
            this.cdr.detectChanges();
            window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
              detail: { isVisible: this.shouldShowBanner }
            }));
          }
        } else {
          this.planName = '';
          this.shouldShowBanner = false;
          this.isLoading = false;
          this.cdr.detectChanges();
          window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
            detail: { isVisible: false }
          }));
        }

        // Check MVE usage (80% warning)
        this.checkMveUsage(parkingId);
      },
      error: () => {
        if (this.isLoading) {
          this.shouldShowBanner = false;
          this.isLoading = false;
          this.cdr.detectChanges();
          window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
            detail: { isVisible: false }
          }));
        }
        // Fallback: try API-based MVE check
        this.checkMveUsage(parkingId);
      }
    });
  }

  private checkMveUsage(parkingId: string): void {
    const currentUrl = this.router.url || '';
    if (this.isExcludedUrl(currentUrl)) {
      this.showMveWarning = false;
      return;
    }

    this.apiService.checkMaxMVELimit(parkingId).subscribe({
      next: ({ maxMve, currentCount }) => {
        if (maxMve && maxMve > 0 && maxMve < 999999) {
          const rawPercent = Math.round((currentCount / maxMve) * 100);
          this.mveUsagePercent = Math.min(rawPercent, 100); // Cap at 100% for UI purposes
          this.showMveWarning = rawPercent >= 80;
          if (this.showMveWarning) {
            this.shouldShowBanner = true;
            window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
              detail: { isVisible: true }
            }));
          }
        } else {
          this.showMveWarning = false;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.showMveWarning = false;
        this.cdr.detectChanges();
      }
    });
  }

  private processSubscriptionData(subscription: any, parkingId?: string): void {
    // Reset flags
    this.hasNoActivePlan = false;

    // Normalize status
    const normalizedStatus = this.normalizeSubscriptionStatus(subscription.status);
    const isCancelledStatus = ['cancelled', 'canceled', 'cancel', 'inactive'].includes(normalizedStatus);
    const isTrialStatus = normalizedStatus === 'trial' || normalizedStatus === 'trialing';

    // Normalize trial flag
    const isTrial = subscription.is_trial === true ||
      subscription.is_trial === 1 ||
      subscription.is_trial === 'true' ||
      subscription.is_trial === '1' ||
      isTrialStatus;

    // Expiry calculation: If status is active OR end_date/next_billing_date is in future, plan is NOT expired
    const now = Date.now();
    let expiryDate = subscription.end_date || subscription.next_billing_date || subscription.trial_end_date || '';
    let hasFutureEndDate = false;

    const endDate = this.parseDateString(subscription.end_date);
    if (endDate && endDate.getTime() > now) {
      hasFutureEndDate = true;
      expiryDate = subscription.end_date;
    }
    if (!hasFutureEndDate) {
      const nextBilling = this.parseDateString(subscription.next_billing_date);
      if (nextBilling && nextBilling.getTime() > now) {
        hasFutureEndDate = true;
        expiryDate = subscription.next_billing_date;
      }
    }

    if (subscription.isExpired !== undefined) {
      this.isExpired = Boolean(subscription.isExpired);
    } else if (hasFutureEndDate) {
      this.isExpired = false;
    } else if (subscription.trial_end_date) {
      const trialEnd = this.parseDateString(subscription.trial_end_date);
      this.isExpired = !trialEnd || trialEnd.getTime() <= now;
    } else if (endDate) {
      this.isExpired = endDate.getTime() <= now;
    } else if (subscription.is_expired === true || subscription.is_expired === 'true' || subscription.is_expired === 1) {
      this.isExpired = true;
    } else {
      this.isExpired = normalizedStatus === 'expired' || normalizedStatus === 'inactive' || isCancelledStatus;
    }

    this.expiryDate = expiryDate;

    // Calculate days remaining
    if (expiryDate) {
      this.trialDaysRemaining = this.calculateDaysRemaining(expiryDate);
    } else {
      this.trialDaysRemaining = 0;
    }

    // Show trial expiring banner only within 5 days before expiration
    const showForTrial = isTrial && !this.isExpired && this.trialDaysRemaining <= 5;
    const showForExpired = this.isExpired;
    const showForCancelled = isCancelledStatus;

    // Only set hasNoActivePlan when status is truly cancelled/inactive AND plan is NOT just expired
    // If the plan has valid expiry dates and is expired, show "expired" message instead of "no active plan"
    if (showForCancelled && !showForExpired) {
      this.hasNoActivePlan = true;
    }

    const currentUrl = this.router.url || '';
    if (this.isExcludedUrl(currentUrl)) {
      this.shouldShowBanner = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
        detail: { isVisible: false }
      }));
      return;
    }

    this.shouldShowBanner = showForTrial || showForExpired || showForCancelled || this.showMveWarning;
    this.isLoading = false;
    this.cdr.detectChanges();

    window.dispatchEvent(new CustomEvent('bannerVisibilityChanged', {
      detail: { isVisible: this.shouldShowBanner }
    }));

    // Persist full banner state to localStorage and sessionStorage for cross-route consistency
    if (parkingId) {
      const bannerState = {
        shouldShowBanner: this.shouldShowBanner,
        isExpired: this.isExpired,
        hasNoActivePlan: this.hasNoActivePlan,
        planName: this.planName,
        expiryDate: this.expiryDate,
        trialDaysRemaining: this.trialDaysRemaining
      };
      localStorage.setItem(`parking_banner_state_${parkingId}`, JSON.stringify(bannerState));
      sessionStorage.setItem(`parking_banner_state_${parkingId}`, JSON.stringify(bannerState));
    }
  }

  private normalizeSubscriptionStatus(status: any): string {
    if (typeof status === 'string') {
      return status.toLowerCase();
    }
    if (status === false || status === 0) {
      return 'inactive';
    }
    if (status === true || status === 1) {
      return 'active';
    }
    return '';
  }

  private parseDateString(dateStr: string | Date | null | undefined): Date | null {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return isNaN(dateStr.getTime()) ? null : dateStr;
    try {
      const raw = String(dateStr).trim();
      const native = new Date(raw);
      if (!isNaN(native.getTime())) return native;

      const withT = new Date(raw.replace(' ', 'T'));
      if (!isNaN(withT.getTime())) return withT;

      // Safari rejects "yyyy-mm-dd hh:mm:ss" and ISO without timezone.
      const safariSafe = raw
        .replace('T', ' ')
        .replace(/[zZ]$/, '')
        .replace(/([+-]\d{2}:?\d{2})$/, '')
        .replace(/-/g, '/')
        .replace(/\.\d+/, '');
      const parsed = new Date(safariSafe);
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  }

  private calculateDaysRemaining(endDate: string | Date): number {
    const trialEnd = this.parseDateString(endDate);
    if (!trialEnd) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    trialEnd.setHours(0, 0, 0, 0);

    const timeDiff = trialEnd.getTime() - today.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  protected navigateToPlan(): void {
    const parkingId = this.getCurrentParkingId();
    if (parkingId) {
      this.router.navigate(['/parking', parkingId, 'upgrade-plan']);
    } else {
      this.router.navigate(['/upgrade-plan']);
    }
  }

  protected navigateToUpgrade(): void {
    const parkingId = this.getCurrentParkingId();
    if (parkingId) {
      this.router.navigate(['/parking', parkingId, 'upgrade-plan']);
    } else {
      this.router.navigate(['/upgrade-plan']);
    }
  }

  private getCurrentParkingId(): string | null {
    const urlParkingId = this.getCurrentUrlParkingId();
    if (urlParkingId) return urlParkingId;

    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const stored = sessionStorage.getItem('parking_id') ||
      localStorage.getItem('parking_id') ||
      sessionStorage.getItem('selectedParkingId') ||
      localStorage.getItem('selectedParkingId');
    return (stored && uuidPattern.test(stored)) ? stored : (stored || null);
  }

  private getCurrentUrlParkingId(): string | null {
    const url = this.router.url || '';
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const segments = url.split('?')[0].split('#')[0].split('/').filter(seg => seg.length > 0);
    const parkingIdx = segments.findIndex(s => s.toLowerCase() === 'parking' || s.toLowerCase() === 'vendor');
    if (parkingIdx !== -1 && segments.length > parkingIdx + 1 && uuidPattern.test(segments[parkingIdx + 1])) {
      return segments[parkingIdx + 1];
    }
    const match = url.match(/\/parking\/([0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})(?:\/|$|\?)/i);
    return match ? match[1] : null;
  }
}
